import React from 'react';
import { Swords, Trophy } from 'lucide-react';
import { ChallengeData } from '../../services/challengeService';
import logo from '../../assets/logo.png';

interface ChallengeShareCardProps {
  challenge: ChallengeData | null;
  currentUser: { uid: string; displayName: string | null; photoURL?: string | null } | null;
  cardRef: React.RefObject<HTMLDivElement>;
}

export default function ChallengeShareCard({ challenge, currentUser, cardRef }: ChallengeShareCardProps) {
  if (!challenge) return null;

  const isCompleted = challenge.status === 'completed' || (challenge.endTime && challenge.endTime < Date.now());
  const typeLabel = challenge.challengeType === 'crime_game' ? 'Crime Game' :
                    challenge.challengeType === 'daily_training' ? 'Training' :
                    challenge.challengeType === 'word_of_day' ? 'Word of Day' : 'Point Race';

  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="fixed top-[200vh] left-[200vw] pointer-events-none z-[-100]">
      <div ref={cardRef} className="w-[800px] h-auto min-h-[600px] bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-950 flex flex-col items-center justify-center px-12 py-16 font-sans text-white relative overflow-visible">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 4px 4px, white 2px, transparent 0)', backgroundSize: '48px 48px' }}></div>
        
        <div className="relative z-10 flex w-full items-center justify-center gap-4 mb-8">
          <img referrerPolicy="no-referrer" src={logo} alt="Logo" className="absolute left-0 w-24 h-24 top-1/2 -translate-y-1/2" crossOrigin="anonymous" />
          {isCompleted ? <Trophy className="w-16 h-16 text-yellow-400" /> : <Swords className="w-16 h-16 text-yellow-400" />}
          <h1 className="text-5xl font-black tracking-tight uppercase">{isCompleted ? 'Challenge Result!' : 'Active Challenge!'}</h1>
        </div>
        
        <div className="relative z-10 flex items-center justify-center w-full gap-8 bg-white/10 p-8 rounded-3xl backdrop-blur-md border border-white/20 shadow-2xl">
          {/* Challenger */}
          <div className="flex flex-col items-center flex-1 z-10">
            <div className="relative mb-6">
              <div className={`w-32 h-32 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center text-4xl font-bold border-4 shadow-xl ${isCompleted && challenge.winnerId === challenge.challengerId ? 'border-yellow-400' : 'border-white'}`}>
                {challenge.challengerPhoto ? (
                  <img referrerPolicy="no-referrer" src={challenge.challengerPhoto} alt={challenge.challengerName} className="w-full h-full object-cover rounded-full" crossOrigin="anonymous" />
                ) : (
                  <span className="text-white text-5xl font-black drop-shadow-md">{getInitials(challenge.challengerName)}</span>
                )}
              </div>
              {isCompleted && challenge.winnerId === challenge.challengerId && (
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-yellow-400 px-4 py-1.5 rounded-full shadow-lg border-2 border-yellow-200 z-20">
                  <span className="text-yellow-900 font-black text-sm uppercase tracking-widest whitespace-nowrap">Winner!</span>
                </div>
              )}
              {isCompleted && challenge.winnerId === challenge.challengerId && (
                <div className="absolute -top-3 -right-3 bg-yellow-400 p-2 rounded-full shadow-lg z-20">
                  <Trophy className="w-6 h-6 text-yellow-900" />
                </div>
              )}
            </div>
            <p className="text-3xl font-bold text-center w-full leading-relaxed px-2 pb-2 h-auto drop-shadow-md">{challenge.challengerName}</p>
            <div className="mt-2 bg-white/20 w-32 h-24 rounded-xl backdrop-blur-sm border border-white/10 flex flex-col items-center justify-center shadow-inner">
               <p className="text-sm font-bold uppercase tracking-widest text-indigo-100 mb-1 leading-none drop-shadow-sm">Score</p>
               <p className="text-5xl font-black text-white drop-shadow-md leading-none">{Math.round(challenge.challengerScore || 0)}</p>
            </div>
          </div>
          
          {/* VS */}
          <div className="flex flex-col items-center px-4">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm border border-white/30 shadow-inner pb-1">
              <p className="text-3xl font-black italic text-white leading-none">VS</p>
            </div>
            <div className="mt-4 font-bold text-white/70 uppercase tracking-widest text-sm bg-white/10 px-4 h-8 flex items-center justify-center rounded-full border border-white/10 whitespace-nowrap pb-1">
              <span className="leading-none">{typeLabel} • {formatDate(challenge.startTime)}</span>
            </div>
          </div>
          
          {/* Challenged */}
          <div className="flex flex-col items-center flex-1 z-10">
            <div className="relative mb-6">
              <div className={`w-32 h-32 rounded-full bg-gradient-to-r from-pink-400 to-rose-500 flex items-center justify-center text-4xl font-bold border-4 shadow-xl ${isCompleted && challenge.winnerId === challenge.challengedId ? 'border-yellow-400' : 'border-white'}`}>
                {challenge.challengedPhoto ? (
                  <img referrerPolicy="no-referrer" src={challenge.challengedPhoto} alt={challenge.challengedName} className="w-full h-full object-cover rounded-full" crossOrigin="anonymous" />
                ) : (
                  <span className="text-white text-5xl font-black drop-shadow-md">{getInitials(challenge.challengedName)}</span>
                )}
              </div>
              {isCompleted && challenge.winnerId === challenge.challengedId && (
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-yellow-400 px-4 py-1.5 rounded-full shadow-lg border-2 border-yellow-200 z-20">
                  <span className="text-yellow-900 font-black text-sm uppercase tracking-widest whitespace-nowrap">Winner!</span>
                </div>
              )}
              {isCompleted && challenge.winnerId === challenge.challengedId && (
                <div className="absolute -top-3 -right-3 bg-yellow-400 p-2 rounded-full shadow-lg z-20">
                  <Trophy className="w-6 h-6 text-yellow-900" />
                </div>
              )}
            </div>
            <p className="text-3xl font-bold text-center w-full leading-relaxed px-2 pb-2 h-auto drop-shadow-md">{challenge.challengedName}</p>
            <div className="mt-2 bg-white/20 w-32 h-24 rounded-xl backdrop-blur-sm border border-white/10 flex flex-col items-center justify-center shadow-inner">
               <p className="text-sm font-bold uppercase tracking-widest text-pink-100 mb-1 leading-none drop-shadow-sm">Score</p>
               <p className="text-5xl font-black text-white drop-shadow-md leading-none">{Math.round(challenge.challengedScore || 0)}</p>
            </div>
          </div>
        </div>
        
        <div className="relative z-10 mt-10 flex flex-col items-center gap-4 w-full">
           <div className="flex items-center gap-3 bg-black/40 px-8 py-3 rounded-full backdrop-blur-md shadow-xl border border-white/10">
              <span className="text-white/90 font-bold text-xl drop-shadow-md">Play on LinguaLearn</span>
              <span className="text-white/50 text-xl">•</span>
              <span className="text-white/80 font-medium text-lg tracking-wide">https://lingualearn.web.app</span>
           </div>
           <div className="flex items-center justify-center text-white/90 font-medium text-xl bg-white/10 px-8 py-3 rounded-full border border-white/20 shadow-xl backdrop-blur-sm w-max mx-auto">
             <span className="leading-relaxed drop-shadow-md">Add me as friend to play: <span className="font-black text-white ml-2 px-3 py-1 bg-white/20 rounded-lg tracking-wider">{currentUser?.displayName || 'User'}</span></span>
           </div>
        </div>
      </div>
    </div>
  );
}
